import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Input, Label, Table } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { TIPOS_CARGO } from "./CargoContenedorModal";

const URL_CARGOS = process.env.REACT_APP_URL_CARGOS_CONTENEDOR;
const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;

const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });
const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(v) || 0);

// Estado de cuenta de cargos: Liberación y Flete llevan cada uno su saldo y sus movimientos
export default function EstadoCuentaCargos() {
  const { user } = useContext(AuthContext);
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState("");
  const [tipo, setTipo] = useState("Liberacion");
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    axios.get(URL_CLIENTES, auth()).then(({ data }) => setClientes(data)).catch(() => setClientes([]));
  }, []);

  useEffect(() => {
    setDatos(null);
    if (!cliente) return;
    axios
      .get(`${URL_CARGOS}EstadoCuenta/${cliente}`, auth())
      .then(({ data }) => setDatos(data))
      .catch((err) => Swal.fire({ icon: "error", title: "No se pudo cargar", text: err?.response?.data?.message || err.message }));
  }, [cliente]);

  const etiqueta = (TIPOS_CARGO.find((x) => x.value === tipo) || {}).label;
  const movimientos = datos ? datos.movimientos[tipo] : [];
  const c = datos ? datos.cliente : {};

  const pdf = () => {
    const doc = new jsPDF("landscape");
    doc.text(`Estado de cuenta ${etiqueta} - ${c.nombre_comercial}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Cargos ${fmt(c[`cargos${tipo}`])}   Abonos ${fmt(c[`abonos${tipo}`])}   Saldo ${fmt(c[`saldo${tipo}`])}`, 14, 25);
    doc.autoTable({
      head: [["Fecha", "Movimiento", "Numero", "Contenedor", "Referencia", "Detalle", "Cargo", "Abono", "Saldo"]],
      body: movimientos.map((m) => [m.fecha, m.tipo, m.numero, m.contenedor, m.referencia, m.detalle, fmt(m.cargo), fmt(m.abono), fmt(m.saldo)]),
      startY: 30,
    });
    doc.save(`EstadoCuenta-${etiqueta}-${c.nombre_comercial}.pdf`);
  };

  const excel = () => {
    const filas = movimientos.map((m) => ({
      Fecha: m.fecha, Movimiento: m.tipo, Numero: m.numero, Contenedor: m.contenedor, Referencia: m.referencia,
      Detalle: m.detalle, Cargo: m.cargo, Abono: m.abono, Saldo: m.saldo,
    }));
    const cols = Object.keys(filas[0] || { Fecha: "" });
    new ExportJsonExcel({ fileName: `EstadoCuenta-${etiqueta}-${c.nombre_comercial}`, datas: [{ sheetData: filas, sheetName: etiqueta, sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  };

  if (!user.menuAdmin) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-sm-11 rc-page">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-layer-group" /></span>
              Estado de cuenta de cargos
            </h3>
            <p className="page-subtitle">Liberación y Flete con su propio saldo y sus propios movimientos</p>
          </div>
          <div className="page-actions">
            <Button size="sm" color="secondary" href="/Finanzas?tab=consultas"><i className="fas fa-arrow-left" /> Finanzas</Button>
            <Button size="sm" color="danger" onClick={pdf} disabled={!movimientos.length}><i className="far fa-file-pdf" /> PDF</Button>
            <Button size="sm" onClick={excel} disabled={!movimientos.length}><i className="far fa-file-excel" /> Excel</Button>
          </div>
        </div>

        <div className="filter-bar">
          <div>
            <Label>Cliente</Label>
            <Input bsSize="sm" type="select" value={cliente} onChange={(e) => setCliente(e.target.value)}>
              <option value="">Selecciona</option>
              {[...clientes].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((a) => (
                <option key={a._id} value={a._id}>{a.nombre_comercial}</option>
              ))}
            </Input>
          </div>
        </div>

        {datos ? (
          <>
            <div className="ec-tipos">
              {TIPOS_CARGO.map((x) => (
                <button key={x.value} type="button" className={`ec-tipo${tipo === x.value ? " on" : ""}`} onClick={() => setTipo(x.value)}>
                  <span className="ec-tipo-titulo">{x.label}</span>
                  <span className="ec-tipo-saldo">{fmt(c[`saldo${x.value}`])}</span>
                  <span className="ec-tipo-detalle">Cargos {fmt(c[`cargos${x.value}`])} · Abonos {fmt(c[`abonos${x.value}`])}</span>
                </button>
              ))}
            </div>

            {movimientos.length === 0 ? (
              <div className="rc-state"><i className="fas fa-check-circle" /><div>Este cliente no tiene movimientos de {etiqueta}.</div></div>
            ) : (
              <Table size="sm" hover className="table-responsive-xl">
                <thead>
                  <tr>
                    <th>Fecha</th><th>Movimiento</th><th>Número</th><th>Contenedor</th><th>Referencia</th><th>Detalle</th>
                    <th className="td-num">Cargo</th><th className="td-num">Abono</th><th className="td-num">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m, i) => (
                    <tr key={i}>
                      <td>{m.fecha}</td>
                      <td><span className={`badge-soft badge-soft-${m.tipo === "Abono" ? "success" : "warning"}`}>{m.tipo}</span></td>
                      <td>{m.numero}</td>
                      <td className="td-strong">{m.contenedor}</td>
                      <td>{m.referencia}</td>
                      <td>{m.detalle}</td>
                      <td className="td-num td-money">{m.cargo ? fmt(m.cargo) : "—"}</td>
                      <td className="td-num td-money">{m.abono ? fmt(m.abono) : "—"}</td>
                      <td className="td-num td-money">{fmt(m.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <div className="rc-state"><i className="fas fa-user" /><div>Selecciona un cliente para ver sus saldos.</div></div>
        )}
      </div>
    </>
  );
}
